import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Portal } from './Portal';

interface TransferStatusSelectProps {
  value: string | null;
  onChange: (value: string | null) => Promise<void>;
}

// Workflow values for Interested-tab rows. Mirrors the CHECK constraint on
// inbox_meetings.transfer_status (migration
// 20260511_inbox_meetings_transfer_status_and_promoted_at.sql).
const STATUS_TRANSLATION_MAP: Record<string, string> = {
  'Pendiente': 'transferStatus.pending',
  'Contactado por email': 'transferStatus.contactedEmail',
  'Contactado por llamada': 'transferStatus.contactedCall',
  'Propuesta enviada': 'transferStatus.proposalSent',
  'Sin interés': 'transferStatus.noInterest',
  'Sin respuesta': 'transferStatus.noResponse',
};

// Palette mirrors StatusSelect — same blue/green/red families so the two
// dropdowns read as members of one design system. Progression maps roughly:
// neutral → light blue → deeper blue → green → red.
const STATUS_OPTIONS = [
  { value: 'Pendiente', color: 'bg-gray-100 text-gray-800' },
  { value: 'Contactado por email', color: 'bg-blue-100 text-blue-800' },
  { value: 'Contactado por llamada', color: 'bg-blue-200 text-blue-900' },
  { value: 'Propuesta enviada', color: 'bg-green-100 text-green-800' },
  { value: 'Sin interés', color: 'bg-red-200 text-red-800' },
  { value: 'Sin respuesta', color: 'bg-gray-300 text-gray-800' },
];

const VIEWPORT_MARGIN = 8;

export const TransferStatusSelect: React.FC<TransferStatusSelectProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; visible: boolean }>({
    top: 0,
    left: 0,
    visible: false,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentStatus = STATUS_OPTIONS.find(s => s.value === value);

  const getStatusLabel = (statusValue: string | null): string => {
    if (!statusValue) return t('transferStatus.selectStatus');
    const key = STATUS_TRANSLATION_MAP[statusValue];
    return key ? t(key) : statusValue;
  };

  const handleStatusChange = async (newStatus: string | null) => {
    setIsOpen(false);
    try {
      await onChange(newStatus);
    } catch (error) {
      console.error('Error updating transfer status:', error);
    }
  };

  const updatePosition = () => {
    if (!buttonRef.current || !dropdownRef.current) return;
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current.offsetHeight;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    const placeAbove = spaceBelow < dropdownHeight + VIEWPORT_MARGIN && spaceAbove > spaceBelow;
    const top = placeAbove
      ? Math.max(VIEWPORT_MARGIN, buttonRect.top - dropdownHeight - 4)
      : buttonRect.bottom + 4;

    setPosition({ top, left: buttonRect.left, visible: true });
  };

  useLayoutEffect(() => {
    if (isOpen) updatePosition();
    else setPosition(prev => ({ ...prev, visible: false }));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handle = () => updatePosition();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        dropdownRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`w-full flex items-center justify-between px-2.5 py-1 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-[#0055FF] transition-colors ${
          currentStatus?.color || 'bg-gray-50 text-gray-500'
        }`}
      >
        <span>{getStatusLabel(value)}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <Portal>
          <div
            ref={dropdownRef}
            className="fixed z-[1000] bg-white shadow-lg border border-gray-200 p-2 min-w-[220px]"
            style={{
              top: position.top,
              left: position.left,
              visibility: position.visible ? 'visible' : 'hidden',
            }}
          >
            <div className="flex flex-col gap-1.5">
              <button
                key="clear-status"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusChange(null);
                }}
                className={`text-left px-3 py-1.5 text-xs font-medium transition-colors bg-gray-100 text-gray-700 ${
                  value === null ? 'ring-2 ring-[#0055FF] ring-opacity-50' : ''
                } hover:opacity-90`}
              >
                {t('transferStatus.clearStatus')}
              </button>
              {STATUS_OPTIONS.map(status => (
                <button
                  key={status.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusChange(status.value);
                  }}
                  className={`text-left px-3 py-1.5 text-xs font-medium transition-colors ${
                    status.color
                  } ${value === status.value ? 'ring-2 ring-[#0055FF] ring-opacity-50' : ''} hover:opacity-90`}
                >
                  {getStatusLabel(status.value)}
                </button>
              ))}
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};
